import React from "react";
import _ from "lodash";
import moment from "moment";
import styled from "styled-components";
import theme from "../../../../assets/theme.js";
import {
  TopCard,
  PartyAccentBar,
  PartyHeaderContent,
  Row,
  PartyHeaderLabel,
  StageHeaderTitle,
  Badge,
  StageCardHeader,
} from "./CaseDetails.js";

const STATUS_PALETTE = [
  { bg: "#eff6ff", accent: "#3b82f6", text: "#1e40af" },
  { bg: "#f0fdfa", accent: "#14b8a6", text: "#0f766e" },
  { bg: "#fdf4ff", accent: "#c026d3", text: "#86198f" },
  { bg: "#fff7ed", accent: "#fb923c", text: "#9a3412" },
  { bg: "#f5f3ff", accent: "#8b5cf6", text: "#5b21b6" },
  { bg: "#ecfeff", accent: "#06b6d4", text: "#155e75" },
  { bg: "#fefce8", accent: "#eab308", text: "#854d0e" },
  { bg: "#fdf2f8", accent: "#ec4899", text: "#9d174d" },
];

const hashKeyToIndex = (str, length) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % length;
};

const getStatusColor = (key = "") => {
  const k = key.toLowerCase();
  if (k.includes("draft"))
    return { bg: "#f1f5f9", accent: "#94a3b8", text: "#475569" };
  if (k.includes("settled") || k.includes("approved") || k.includes("closed"))
    return { bg: "#f0fdf4", accent: "#22c55e", text: "#15803d" };
  if (k.includes("reject") || k.includes("cancel") || k.includes("withdraw"))
    return { bg: "#fef2f2", accent: "#ef4444", text: "#b91c1c" };
  if (k.includes("pending") || k.includes("fee") || k.includes("waiting"))
    return { bg: "#fffbeb", accent: "#f59e0b", text: "#92400e" };
  return STATUS_PALETTE[hashKeyToIndex(key, STATUS_PALETTE.length)];
};

const parseHistoryDate = (date) => {
  if (!date) return null;
  const parsed = moment(
    date,
    ["DD/MM/YYYY HH:mm:ss", "YYYY-MM-DD HH:mm:ss"],
    true,
  );
  return parsed.isValid() ? parsed : null;
};

const formatHistoryDateTime = (date) => {
  const parsed = parseHistoryDate(date);
  return parsed
    ? parsed.format("DD MMM YYYY, hh:mm A")
    : date || "Not available";
};

const buildStatusHistory = (caseStatusUpdatedTime) => {
  return Object.entries(caseStatusUpdatedTime || {})
    .map(([key, value]) => {
      const hearingDate = value?.date || null;
      const statusUpdateDate = value?.updatedDate || null;
      return {
        key,
        label: key.includes(" ") ? key : _.startCase(key),
        hearingDate,
        statusUpdateDate,
        rawDate: statusUpdateDate || hearingDate,
        parsedDate: parseHistoryDate(statusUpdateDate || hearingDate),
        name: value?.name || null,
        remarks: value?.remarks || null,
      };
    })
    .sort((a, b) => {
      if (a.parsedDate && b.parsedDate) return a.parsedDate - b.parsedDate;
      if (a.parsedDate) return -1;
      if (b.parsedDate) return 1;
      return 0;
    });
};

const CaseStatusHistoryCard = ({ caseObj }) => {
  const statusHistory = buildStatusHistory(caseObj?.caseStatusUpdatedTime);

  if (!statusHistory.length) return null;

  return (
    <TopCard>
      <StageCardHeader>
        <PartyAccentBar color="#006970" />
        <PartyHeaderContent>
          <Row gap="8px" style={{ justifyContent: "space-between", flex: 1 }}>
            <PartyHeaderLabel>Case Status History</PartyHeaderLabel>
            <Badge bg="#E6F2F3" color="#006970">
              {statusHistory.length} change{statusHistory.length > 1 ? "s" : ""}
            </Badge>
          </Row>
          <StageHeaderTitle>Full timeline of status updates</StageHeaderTitle>
        </PartyHeaderContent>
      </StageCardHeader>

      <StatusHistoryBody>
        <StatusHistoryList>
          {statusHistory.map((entry, idx) => {
            const color = getStatusColor(entry.key);
            return (
              <StatusHistoryCard
                key={`${entry.key}-${idx}`}
                style={{
                  background: color.bg,
                  borderLeft: `4px solid ${color.accent}`,
                }}
              >
                <StatusHistoryTop>
                  <StatusHistoryHeaderRow>
                    <StatusHistoryLabel style={{ color: color.text }}>
                      {entry.label}
                    </StatusHistoryLabel>
                    <StatusHistoryBadge style={{ background: color.accent }}>
                      {idx + 1}
                    </StatusHistoryBadge>
                  </StatusHistoryHeaderRow>

                  {(entry.hearingDate || entry.statusUpdateDate) && (
                    <StatusHistoryDates>
                      {entry.hearingDate && (
                        <StatusHistoryMetaRow>
                          <span>Status Date</span>
                          <strong>
                            {formatHistoryDateTime(entry.hearingDate)}
                          </strong>
                        </StatusHistoryMetaRow>
                      )}
                      {entry.statusUpdateDate && (
                        <StatusHistoryMetaRow>
                          <span>Updated At</span>
                          <strong>
                            {formatHistoryDateTime(entry.statusUpdateDate)}
                          </strong>
                        </StatusHistoryMetaRow>
                      )}
                    </StatusHistoryDates>
                  )}
                </StatusHistoryTop>

                {(entry.name || entry.remarks) && (
                  <StatusHistoryMeta>
                    {entry.name && (
                      <StatusHistoryMetaRow>
                        <span>By</span>
                        <strong>{entry.name}</strong>
                      </StatusHistoryMetaRow>
                    )}
                    {entry.remarks && (
                      <StatusHistoryMetaRow>
                        <span>Remarks</span>
                        <strong>{entry.remarks}</strong>
                      </StatusHistoryMetaRow>
                    )}
                  </StatusHistoryMeta>
                )}
              </StatusHistoryCard>
            );
          })}
        </StatusHistoryList>
      </StatusHistoryBody>
    </TopCard>
  );
};

export default CaseStatusHistoryCard;

const StatusHistoryBody = styled.div`
  padding: 16px 18px 18px;
  @media (max-width: 640px) {
    padding: 12px 12px 14px;
  }
`;

const StatusHistoryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
  grid-auto-rows: 1fr;
  gap: 10px;
`;

const StatusHistoryCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e8eef4;
  min-width: 0;
`;

const StatusHistoryTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatusHistoryHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const StatusHistoryLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12.5px;
  line-height: 1.3;
`;

const StatusHistoryBadge = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #ffffff;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  line-height: 1;
`;

const StatusHistoryDates = styled.div`
  display: grid;
  gap: 2px;
`;

const StatusHistoryMeta = styled.div`
  display: grid;
  gap: 3px;
  padding-top: 8px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
`;

const StatusHistoryMetaRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  color: #64748b;
  font-size: 11.5px;

  strong {
    overflow-wrap: anywhere;
    color: #293461;
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-weight: normal;
    text-align: right;
  }
`;
